import React, { Component } from 'react';
import { connect } from 'react-redux';
import ReactEcharts from 'echarts-for-react';
import Select from 'react-select';
import './RollingHorizons.css';
import { timelineColors } from './palette';
import i18n from '../../../i18n';

// Constants for mapping chart types to their respective nodes
const CHART_TYPE_TO_NODE = {
  rollingPerformance: 'rollingPerformanceMonthly',
  rollingVolatility: 'rollingStdRisk',
  rollingBeta: 'rollingBeta',
  rollingCorrelations: 'rollingPerformanceMonthly',
  rollingTrackingError: 'rollingTrackingError', // Correctly mapped
};

// Existing Roll Window Mappings
const ROLL_WINDOW_TO_DATE_FIELD = {
  1: 'yrs1NAVDate',
  3: 'yrs3NAVDate',
  5: 'yrs5NAVDate',
  10: 'yrs10NAVDate',
};

const ROLL_WINDOW_TO_DATA_FIELD = {
  1: 'yrs1NAV',
  3: 'yrs3NAV',
  5: 'yrs5NAV',
  10: 'yrs10NAV',
};

// Utility Functions
function formatDate(date) {
  if (!date || isNaN(date)) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

function calculatePearsonCorrelation(x, y) {
  const n = x.length;
  if (n === 0) return null;

  const sumX = x.reduce((a, b) => a + b, 0);
  const sumY = y.reduce((a, b) => a + b, 0);

  const sumX2 = x.reduce((a, b) => a + b * b, 0);
  const sumY2 = y.reduce((a, b) => a + b * b, 0);

  const sumXY = x.reduce((a, b, idx) => a + b * y[idx], 0);

  const numerator = n * sumXY - sumX * sumY;
  const denominator = Math.sqrt(
    (n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY)
  );

  if (denominator === 0) return null;
  return numerator / denominator;
}

function dataFormatter(value, type, chartType) {
  if (typeof value !== 'number') {
    return 'N/A';
  }

  if (type === 'axis') {
    if (chartType === 'rollingBeta' || chartType === 'rollingCorrelations') {
      return `${value}`;
    }
    return `${value}%`;
  } else if (type === 'data') {
    if (chartType === 'rollingBeta' || chartType === 'rollingCorrelations') {
      return `${value.toFixed(2)}`;
    }
    return `${value.toFixed(2)}%`;
  } else {
    return `${value.toFixed(2)}%`;
  }
}

// Helper function to get dataField and dateField based on chartType and rollWindow
function getFields(chartType, rollWindow) {
  if (chartType === 'rollingTrackingError') {
    return {
      dataField: `yrs${rollWindow}NAV`, // Represents Tracking Error values
      dateField: `yrs${rollWindow}NAVDate`, // Represents Tracking Error dates
    };
  } else if (chartType === 'rollingStdRisk') {
    return {
      dataField: `yrs${rollWindow}StdRisk`,
      dateField: `yrs${rollWindow}StdRiskDate`,
    };
  } else {
    return {
      dataField: ROLL_WINDOW_TO_DATA_FIELD[rollWindow],
      dateField: ROLL_WINDOW_TO_DATE_FIELD[rollWindow],
    };
  }
}

function getStartAndEndDates(data, chartType, rollWindow) {
  const node = CHART_TYPE_TO_NODE[chartType];
  const { dateField } = getFields(chartType, rollWindow);
  let startDate = null;
  let endDate = null;

  for (let i = 0; i < data?.length; i++) {
    const fundData = data[i]?.rollingHorizons?.[node]?.[dateField];
    if (Array.isArray(fundData) && fundData.length > 0) {
      startDate = new Date(fundData[0]);
      endDate = new Date(fundData[fundData.length - 1]);
      break;
    }
  }

  return { startDate, endDate };
}

function getLegendData(filteredData) {
  // Return "Fund Name (Ticker)" instead of just ticker
  return filteredData.map((fund) => `${fund.name} (${fund.ticker})`);
}

// Tooltip Formatter Function
function tooltipFormatter(chartType, params) {
  if (!params) return '';

  let res = '';

  if (chartType === 'rollingCorrelations') {
    if (Array.isArray(params) && params.length > 0) {
      res = `<strong>${params[0].name}</strong>`;
      params
        .filter((item) => item.seriesName.includes('vs')) // show only correlation lines
        .forEach((item) => {
          const value =
            typeof item.value === 'number' ? item.value.toFixed(2) : 'N/A';
          res += `<br/><span style="display:inline-block;margin-right:5px;border-radius:50%;width:10px;height:10px;background-color:${item.color};"></span>${item.seriesName}: ${value}`;
        });
    }
  } else {
    // Existing tooltip formatting for other chart types
    if (Array.isArray(params)) {
      if (params.length === 0) return '';
      res = `<strong>${params[0].name}</strong>`;
      params.forEach((item) => {
        const seriesName = item.seriesName;
        const value = item.value;
        // Check if value is a number before formatting
        const formattedValue =
          typeof value === 'number'
            ? chartType === 'rollingBeta'
              ? value.toFixed(2)
              : dataFormatter(value, 'data', chartType)
            : 'N/A';

        res += `<br/><span style="display:inline-block;margin-right:5px;border-radius:50%;width:10px;height:10px;background-color:${item.color};"></span>${seriesName}: ${formattedValue}`;
      });
    } else {
      const item = params;
      const seriesName = item.seriesName;
      const value = item.value;
      const formattedValue =
        typeof value === 'number'
          ? chartType === 'rollingBeta'
            ? value.toFixed(2)
            : dataFormatter(value, 'data', chartType)
          : 'N/A';

      res = `<strong>${item.name}</strong><br/>
             <span style="display:inline-block;margin-right:5px;border-radius:50%;width:10px;height:10px;background-color:${item.color};"></span>${seriesName}: ${formattedValue}`;
    }
  }

  return res;
}

// Benchmark Extraction Helper Function
function getBenchmarkInfo(data) {
  if (Array.isArray(data) && data.length > 0) {
    // Find the first fund with a non-null bmkName
    const benchmarkFund = data.find((fund) => fund.beta && fund.beta.bmkName);
    if (benchmarkFund) {
      return {
        bmkName: benchmarkFund.beta.bmkName,
        bmkTicker: benchmarkFund.beta.bmkTicker,
      };
    }
  }
  // Default benchmark if none found
  return {
    bmkName: 'S&P 500 Index',
    bmkTicker: '500',
  };
}

// Chart Data Generator
// Dev version of chartData with rollingCorrelations fixed
function chartData(
  data,
  chartType,
  rollWindow,
  primaryFundTicker = null,
  benchmarkFundTicker = null
) {
  const node = CHART_TYPE_TO_NODE[chartType];
  const dataField = ROLL_WINDOW_TO_DATA_FIELD[rollWindow];
  const dateField = ROLL_WINDOW_TO_DATE_FIELD[rollWindow];

  const filteredData = data.filter((fund) => {
    const fundData = fund?.rollingHorizons?.[node]?.[dataField];
    return Array.isArray(fundData) && fundData.length > 0;
  });

  const legendData = getLegendData(filteredData);

  let series = [];
  let chartDates = [];

  if (chartType === 'rollingCorrelations') {
    if (filteredData.length < 2) return null;

    // Ensure benchmarkFundTicker is valid
    let benchmarkFundIndex = filteredData.findIndex(
      (fund) => fund.ticker === benchmarkFundTicker
    );
    if (benchmarkFundIndex === -1) {
      benchmarkFundIndex = 1; // Fallback to second fund
      benchmarkFundTicker = filteredData[benchmarkFundIndex].ticker;
    }

    const benchmarkFund = filteredData[benchmarkFundIndex];
    const benchmarkFundData =
      benchmarkFund?.rollingHorizons?.[node]?.[dataField] || [];
    const benchmarkDates =
      benchmarkFund?.rollingHorizons?.[node]?.[dateField] || [];

    chartDates = benchmarkDates.map((dateStr) => formatDate(new Date(dateStr)));

    // Optional: Add hidden performance lines for tooltip if needed
    filteredData.forEach((fund) => {
      const fundPerfData =
        fund?.rollingHorizons?.[node]?.[dataField]?.map((val) =>
          parseFloat(val * 100)
        ) || [];
      series.push({
        name: `${fund.name} (${fund.ticker})`,
        data: fundPerfData,
        type: 'line',
        lineStyle: { color: 'transparent', opacity: 0 },
        symbol: 'none',
        showInLegend: false,
        silent: true,
        emphasis: { itemStyle: { color: 'transparent' } },
      });
    });

    // Add correlation lines for all funds vs benchmark
    filteredData.forEach((fund, index) => {
      if (fund.ticker === benchmarkFundTicker) return;

      const primaryFundData = fund?.rollingHorizons?.[node]?.[dataField] || [];
      if (primaryFundData.length !== benchmarkFundData.length) return;

      const correlationData = [];
      const windowSize = rollWindow * 12;

      for (let i = 0; i < primaryFundData.length; i++) {
        const windowStart = Math.max(0, i - windowSize + 1);
        const windowEnd = i + 1;

        const windowPrimary = primaryFundData
          .slice(windowStart, windowEnd)
          .map(Number);
        const windowBenchmark = benchmarkFundData
          .slice(windowStart, windowEnd)
          .map(Number);

        if (windowPrimary.length < 2 || windowBenchmark.length < 2) {
          correlationData.push(null);
        } else {
          const correlation = calculatePearsonCorrelation(
            windowPrimary,
            windowBenchmark
          );
          correlationData.push(
            correlation !== null ? parseFloat(correlation.toFixed(2)) : null
          );
        }
      }

      series.push({
        name: `${fund.name} (${fund.ticker}) vs ${benchmarkFund.name} (${benchmarkFund.ticker})`,
        data: correlationData,
        type: 'line',
        smooth: true,
        symbol: 'circle',
        showSymbol: true,
        lineStyle: {
          color: timelineColors[index % timelineColors.length],
        },
        itemStyle: {
          color: timelineColors[index % timelineColors.length],
        },
        emphasis: {
          itemStyle: {
            color: timelineColors[index % timelineColors.length],
          },
        },
      });
    });
  } else {
    // Non-correlation charts (unchanged from your dev)
    series = filteredData.map((fund, index) => {
      let fundData =
        fund?.rollingHorizons?.[node]?.[dataField].map((val) => {
          return chartType === 'rollingBeta'
            ? parseFloat(val)
            : parseFloat(val * 100);
        }) || [];

      const color = timelineColors[index % timelineColors.length];
      return {
        name: `${fund.name} (${fund.ticker})`,
        data: fundData,
        type: 'line',
        smooth: true,
        showSymbol: true,
        symbolSize: 6,
        lineStyle: { color: color, type: 'solid' },
        itemStyle: { color, borderColor: color, borderWidth: 2 },
        emphasis: { itemStyle: { color } },
      };
    });

    const firstValidFund = filteredData[0];
    chartDates = firstValidFund?.rollingHorizons?.[node]?.[dateField] || [];
    chartDates = chartDates.map((dateStr) => formatDate(new Date(dateStr)));
  }

  return {
    series,
    xAxis: {
      type: 'category',
      data: chartDates,
      axisLabel: {
        fontSize: 12,
        color: '#050139',
        formatter: (value) => value,
      },
    },
    yAxis: {
      type: 'value',
      min: chartType === 'rollingCorrelations' ? -1 : undefined,
      max: chartType === 'rollingCorrelations' ? 1 : undefined,
      axisLabel: {
        fontSize: 12,
        color: '#8C959B',
        formatter: (value) => dataFormatter(value, 'axis', chartType),
      },
    },
    graphic: {
      type: 'image',
      id: 'logo',
      right: 5, // Place logo at upper right
      top: 5, // Adjusted top margin
      style: {
        image:
          'https://www.wisdomtree.com/-/media/us-media-files/home/wt-logo-navy-2024.png',
        width: 100,
        height: 40,
      },
    },
    legend: {
      icon: 'circle',
      data:
        chartType === 'rollingCorrelations'
          ? series.filter((s) => s.name.includes(' vs ')).map((s) => s.name)
          : legendData,
      textStyle: { fontSize: 12, color: '#050139' },
      top: '1%',
    },
    tooltip: {
      trigger: 'axis',
      formatter: function (params) {
        return tooltipFormatter(chartType, params);
      },
      backgroundColor: '#FFF',
      borderWidth: 1,
      borderColor: '#050139',
      borderRadius: 0,
      textStyle: {
        color: '#5B6770',
        align: 'left',
      },
    },
    color: timelineColors,
  };
}

class RollingHorizons extends Component {
  state = {
    chartType: {
      value: 'rollingPerformance',
      label: 'Annualized Total Return',
    },
    rollWindow: 1,
    startDate: null,
    endDate: null,
    chartOptions: {},
    fullXAxisData: [],
    inputStartDate: '',
    inputEndDate: '',
    error: '',
    primaryFund: null,
    benchmarkFund: null,
    bmkName: 'S&P 500 Index', // Initialize with default
    bmkTicker: '500', // Initialize with default
  };

  componentDidMount() {
    this.initializeChart();
  }

  componentDidUpdate(prevProps, prevState) {
    // Only re-initialize chart if relevant props or state have changed
    if (
      prevProps.data !== this.props.data ||
      prevState.chartType.value !== this.state.chartType.value ||
      prevState.rollWindow !== this.state.rollWindow ||
      prevState.primaryFund !== this.state.primaryFund ||
      prevState.benchmarkFund !== this.state.benchmarkFund
    ) {
      this.initializeChart();
    }
  }

  initializeChart = () => {
    const { data } = this.props;
    const { chartType, rollWindow, primaryFund, benchmarkFund } = this.state;
    const { startDate, endDate } = getStartAndEndDates(
      data,
      chartType.value,
      rollWindow
    );

    let updatedPrimaryFund = primaryFund;
    let updatedBenchmarkFund = benchmarkFund;

    if (chartType.value === 'rollingCorrelations' && data.length >= 2) {
      // Set default primary and benchmark funds if not set
      if (!primaryFund) {
        updatedPrimaryFund = data[0].ticker;
      }
      if (!benchmarkFund || benchmarkFund === updatedPrimaryFund) {
        const nextFund = data.find(
          (fund) => fund.ticker !== updatedPrimaryFund
        );
        updatedBenchmarkFund = nextFund ? nextFund.ticker : null;
      }
    } else {
      updatedPrimaryFund = null;
      updatedBenchmarkFund = null;
    }

    const chartOptions = chartData(
      data,
      chartType.value,
      rollWindow,
      updatedPrimaryFund,
      updatedBenchmarkFund
    );

    // Fetch fullXAxisData based on the current chart type and roll window
    const { dataField, dateField } = getFields(chartType.value, rollWindow);

    const firstValidFund = data.find(
      (fund) =>
        fund?.rollingHorizons?.[CHART_TYPE_TO_NODE[chartType.value]]?.[
          dataField
        ]?.length > 0
    );

    const fullXAxisData =
      firstValidFund?.rollingHorizons?.[CHART_TYPE_TO_NODE[chartType.value]]?.[
        dateField
      ] || [];
    const formattedFullDates = fullXAxisData.map((dateStr) =>
      formatDate(new Date(dateStr))
    );

    // Updated Benchmark Extraction Logic
    const benchmarkInfo = getBenchmarkInfo(data);

    // Debugging: Log the selected chart type and data fields
    // console.log('Selected Chart Type:', chartType.value);
    // console.log('Data Field:', dataField);
    // console.log('Date Field:', dateField);
    // console.log('Chart Options:', chartOptions);

    // Update state with new chart options, X-axis data, and benchmark info
    this.setState(
      {
        startDate,
        endDate,
        inputStartDate: formatDate(startDate),
        inputEndDate: formatDate(endDate),
        chartOptions,
        fullXAxisData: formattedFullDates,
        error: '',
        primaryFund: updatedPrimaryFund,
        benchmarkFund: updatedBenchmarkFund,
        bmkName: benchmarkInfo.bmkName,
        bmkTicker: benchmarkInfo.bmkTicker,
      },
      () => {
        // Optional: Further debugging
        // console.log('Initialized Chart with:', this.state.chartOptions);
      }
    );
  };

  setRollWindow = (rollWindow) => {
    this.setState({ rollWindow }, this.initializeChart);
  };

  handleChartTypeChange = (chartType) => {
    this.setState((prevState) => {
      let newRollWindow = prevState.rollWindow;
      // If switching to 'rollingBeta' or 'rollingTrackingError' and current rollWindow is 1 or 10, set to 3
      if (
        (chartType.value === 'rollingBeta' ||
          chartType.value === 'rollingTrackingError') &&
        (prevState.rollWindow === 1 || prevState.rollWindow === 10)
      ) {
        newRollWindow = 3;
      }
      return {
        chartType,
        primaryFund: null,
        benchmarkFund: null,
        error: '',
        rollWindow: newRollWindow,
      };
    }, this.initializeChart);
  };

  handleInputChange = (e) => {
    const { name, value } = e.target;
    this.setState({
      [name]: value,
      error: '',
    });
  };

  handleGoClick = () => {
    const { inputStartDate, inputEndDate, fullXAxisData } = this.state;
    const parsedStartDate = new Date(inputStartDate);
    const parsedEndDate = new Date(inputEndDate);

    if (isNaN(parsedStartDate) || isNaN(parsedEndDate)) {
      this.setState({
        error: 'Please enter valid dates in MM/DD/YYYY format.',
      });
      return;
    }

    if (parsedStartDate > parsedEndDate) {
      this.setState({ error: 'Start date must be before End date.' });
      return;
    }

    const startIndex = fullXAxisData.findIndex(
      (date) => date === inputStartDate
    );
    const endIndex = fullXAxisData.findIndex((date) => date === inputEndDate);

    if (startIndex === -1 || endIndex === -1) {
      this.setState({ error: 'Dates are out of range.' });
      return;
    }

    const totalDates = fullXAxisData.length;
    const startPercent = (startIndex / (totalDates - 1)) * 100;
    const endPercent = (endIndex / (totalDates - 1)) * 100;

    if (this.echartsRef && this.echartsRef.getEchartsInstance) {
      this.echartsRef.getEchartsInstance().setOption(
        {
          dataZoom: [
            {
              type: 'inside',
              start: Math.max(0, Math.min(startPercent, 100)),
              end: Math.min(100, Math.max(endPercent, 0)),
            },
            {
              type: 'slider',
              start: Math.max(0, Math.min(startPercent, 100)),
              end: Math.min(100, Math.max(endPercent, 0)),
              handleSize: '100%',
              handleStyle: {
                color: '#050139',
              },
              showDetail: false,
              top: '90%', // Position slider below the chart
            },
          ],
        },
        { notMerge: true }
      );
    } else {
      this.setState({ error: 'Chart instance not available.' });
      return;
    }

    this.setState({
      startDate: parsedStartDate,
      endDate: parsedEndDate,
      error: '',
    });
  };

  onChartEvents = {
    datazoom: () => {
      const echartsInstance = this.echartsRef.getEchartsInstance();
      const option = echartsInstance.getOption();
      const dataZoom = option.dataZoom[0];
      const start = dataZoom.start;
      const end = dataZoom.end;
      const fullXAxisData = this.state.fullXAxisData;

      const totalDates = fullXAxisData.length;
      const startIndex = Math.floor((start / 100) * (totalDates - 1));
      const endIndex = Math.floor((end / 100) * (totalDates - 1));

      const newStartDate = fullXAxisData[startIndex];
      const newEndDate = fullXAxisData[endIndex];

      this.setState({
        inputStartDate: newStartDate,
        inputEndDate: newEndDate,
        startDate: new Date(newStartDate),
        endDate: new Date(newEndDate),
        error: '',
      });
    },
  };

  handlePrimaryFundChange = (selectedOption) => {
    const newPrimaryFund = selectedOption.value;
    const { data } = this.props;

    // Filter available funds based on current chart type and roll window
    const availableFunds = data.filter(
      (fund) =>
        fund?.rollingHorizons?.[
          CHART_TYPE_TO_NODE[this.state.chartType.value]
        ]?.[
          getFields(this.state.chartType.value, this.state.rollWindow).dataField
        ]?.length > 0
    );

    // Ensure the benchmark fund is not the same as the primary fund
    const nextFund = availableFunds.find(
      (fund) => fund.ticker !== newPrimaryFund
    );
    const newBenchmarkFund = nextFund ? nextFund.ticker : null;

    this.setState(
      { primaryFund: newPrimaryFund, benchmarkFund: newBenchmarkFund },
      this.initializeChart
    );
  };

  handleBenchmarkFundChange = (selectedOption) => {
    const newBenchmarkFund = selectedOption.value;
    this.setState({ benchmarkFund: newBenchmarkFund }, this.initializeChart);
  };

  render() {
    const {
      chartType,
      chartOptions,
      rollWindow,
      inputStartDate,
      inputEndDate,
      startDate,
      endDate,
      error,
      primaryFund,
      benchmarkFund,
      bmkName,
    } = this.state;
    const { data } = this.props; // Access data from props

    const isRollingCorrelations = chartType.value === 'rollingCorrelations';
    const availableFunds = data.filter(
      (fund) =>
        fund?.rollingHorizons?.[CHART_TYPE_TO_NODE[chartType.value]]?.[
          getFields(chartType.value, rollWindow).dataField
        ]?.length > 0
    );

    // Include 'rollingTrackingError' in roll window logic
    const showCorrelationSelectors =
      isRollingCorrelations && availableFunds.length >= 2;

    const primaryOptions = showCorrelationSelectors
      ? availableFunds.map((fund) => ({
          value: fund.ticker,
          label: `${fund.name} (${fund.ticker})`, // Display "Fund Name (Ticker)"
        }))
      : [];

    const benchmarkOptions = showCorrelationSelectors
      ? availableFunds
          .filter((fund) => fund.ticker !== primaryFund)
          .map((fund) => ({
            value: fund.ticker,
            label: `${fund.name} (${fund.ticker})`, // Display "Fund Name (Ticker)"
          }))
      : [];

    // Set default selections
    const defaultPrimaryFund =
      showCorrelationSelectors && !primaryFund
        ? availableFunds[0].ticker
        : primaryFund;
    const defaultBenchmarkFund =
      showCorrelationSelectors && !benchmarkFund && availableFunds.length >= 2
        ? availableFunds.find((fund) => fund.ticker !== primaryFund)?.ticker
        : benchmarkFund;

    // Get the name for default primary and benchmark funds to display in select
    const primaryFundName =
      showCorrelationSelectors && defaultPrimaryFund
        ? data.find((fund) => fund.ticker === defaultPrimaryFund)?.name
        : '';
    const benchmarkFundName =
      showCorrelationSelectors && defaultBenchmarkFund
        ? data.find((fund) => fund.ticker === defaultBenchmarkFund)?.name
        : '';

    // Update availableRollWindows to include 'rollingTrackingError'
    const availableRollWindows = [
      'rollingBeta',
      'rollingTrackingError',
    ].includes(chartType.value)
      ? [3, 5]
      : [1, 3, 5, 10];

    return (
      <div className="rolling-horizons">
        {/* Chart Header */}
        <div className="chartHeader">{i18n.t('Rolling Horizons')}</div>

        {/* Dynamic Chart Subheader (Subtitle) */}
        <div>
          {/* Conditional Subheader for rollingBeta */}
          {chartType.value === 'rollingBeta' && (
            <div
              className="chartSubHeader"
              style={{
                textAlign: 'center',
                marginBottom: '20px',
                fontSize: '16px',
                fontWeight: '600',
                color: '#050139',
              }}
            >
              {`Benchmark used is calculation ${bmkName}`}
            </div>
          )}

          {/* Conditional Subheader for rollingTrackingError */}
          {chartType.value === 'rollingTrackingError' && (
            <div
              className="chartSubHeader"
              style={{
                textAlign: 'center',
                marginBottom: '20px',
                fontSize: '16px',
                fontWeight: '600',
                color: '#050139',
              }}
            >
              {`Tracking Error Analysis - ${rollWindow}Y - ${formatDate(
                startDate
              )} to ${formatDate(endDate)}`}
              <br />
              {`Benchmark used is calculation ${bmkName}`}
            </div>
          )}

          {/* Existing Subheader for other chart types */}
          {!['rollingBeta', 'rollingTrackingError'].includes(
            chartType.value
          ) && (
            <div
              className="chartSubHeader"
              style={{
                textAlign: 'center',
                marginBottom: '20px',
                fontSize: '16px',
                fontWeight: '900',
                color: '#050139',
              }}
            >
              {`${chartType.label} - ${rollWindow}Y - ${formatDate(
                startDate
              )} to ${formatDate(endDate)}`}
            </div>
          )}
        </div>

        {/* Controls Row */}
        <div
          className="row"
          style={{
            marginBottom: '20px',
            alignItems: 'center',
          }}
        >
          {/* Left Side: Chart Type Selector */}
          <div
            className="col-md-3"
            style={{ textAlign: 'center', paddingRight: '3px' }}
          >
            <Select
              options={[
                {
                  value: 'rollingPerformance',
                  label: 'Annualized Total Return',
                },
                {
                  value: 'rollingVolatility',
                  label: 'Annualized Std Deviation',
                },
                { value: 'rollingBeta', label: 'Beta' },
                { value: 'rollingCorrelations', label: 'Correlations' },
                { value: 'rollingTrackingError', label: 'Tracking Error' }, // Added Tracking Error Option
              ]}
              value={chartType}
              onChange={(value) => this.handleChartTypeChange(value)}
              styles={{
                container: (provided) => ({
                  ...provided,
                  textAlign: 'center',
                  paddingLeft: '5px',
                  paddingRight: '5px',
                  height: '38px',
                  borderRadius: '0px',
                }),
              }}
              placeholder="Select Chart Type"
              isClearable={false}
            />
          </div>

          {/* Middle: Rolling Window Buttons */}
          <div
            className="col-md-6"
            style={{
              textAlign: 'center',
              paddingLeft: '0px',
              paddingRight: '0px',
            }}
          >
            {availableRollWindows.includes(1) && (
              <button
                className={`dlyPerfbutton ${
                  rollWindow === 1 ? 'selected-other' : 'dlyPerfbutton-inactive'
                }`}
                onClick={() => this.setRollWindow(1)}
                style={{ marginRight: '0px' }}
              >
                1Y
              </button>
            )}
            {availableRollWindows.includes(3) && (
              <button
                className={`dlyPerfbutton ${
                  rollWindow === 3 ? 'selected-other' : 'dlyPerfbutton-inactive'
                }`}
                onClick={() => this.setRollWindow(3)}
                style={{ marginRight: '0px' }}
              >
                3Y
              </button>
            )}
            {availableRollWindows.includes(5) && (
              <button
                className={`dlyPerfbutton ${
                  rollWindow === 5 ? 'selected-other' : 'dlyPerfbutton-inactive'
                }`}
                onClick={() => this.setRollWindow(5)}
                style={{ marginRight: '0px' }}
              >
                5Y
              </button>
            )}
            {availableRollWindows.includes(10) && (
              <button
                className={`dlyPerfbutton ${
                  rollWindow === 10
                    ? 'selected-other'
                    : 'dlyPerfbutton-inactive'
                }`}
                onClick={() => this.setRollWindow(10)}
              >
                10Y
              </button>
            )}
          </div>

          {/* Right Side: Date Inputs and Go Button */}
          <div
            className="col-md-3"
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            <div
              className="rolling-horizons date-picker-container"
              style={{ marginRight: '10px' }}
            >
              <input
                type="text"
                name="inputStartDate"
                value={inputStartDate}
                onChange={this.handleInputChange}
                placeholder="Start Date (MM/DD/YYYY)"
                className="form-control"
                style={{
                  textAlign: 'center',
                  paddingLeft: '5px',
                  paddingRight: '5px',
                  height: '38px',
                  borderRadius: '0px',
                }}
              />
            </div>
            <div
              className="rolling-horizons date-picker-container"
              style={{ marginRight: '10px' }}
            >
              <input
                type="text"
                name="inputEndDate"
                value={inputEndDate}
                onChange={this.handleInputChange}
                placeholder="End Date (MM/DD/YYYY)"
                className="form-control"
                style={{
                  textAlign: 'center',
                  paddingLeft: '5px',
                  paddingRight: '5px',
                  height: '38px',
                  borderRadius: '0px',
                }}
              />
            </div>
            <button
              className={`dlyPerfbutton dlyPerfGoButton `}
              onClick={this.handleGoClick}
              style={{ height: '38px' }}
            >
              GO
            </button>
          </div>
        </div>

        {/* Correlation Selectors */}
        {showCorrelationSelectors && (
          <div
            className="row"
            style={{ marginBottom: '20px', marginTop: '30px' }}
          >
            <div
              className="col-md-6"
              style={{ textAlign: 'left', paddingRight: '10px' }}
            >
              <Select
                options={primaryOptions}
                value={
                  defaultPrimaryFund
                    ? {
                        value: defaultPrimaryFund,
                        label: `${primaryFundName} (${defaultPrimaryFund})`,
                      }
                    : null
                }
                onChange={this.handlePrimaryFundChange}
                placeholder="Select Primary Fund"
                styles={{
                  container: (provided) => ({
                    ...provided,
                    width: '90%',
                    marginLeft: '0',
                    textAlign: 'center',
                  }),
                }}
                isClearable={false}
              />
            </div>
            <div
              className="col-md-6"
              style={{ textAlign: 'right', paddingLeft: '10px' }}
            >
              <Select
                options={benchmarkOptions}
                value={
                  defaultBenchmarkFund
                    ? {
                        value: defaultBenchmarkFund,
                        label: `${benchmarkFundName} (${benchmarkFund})`,
                      }
                    : null
                }
                onChange={this.handleBenchmarkFundChange}
                placeholder="Select Benchmark Fund"
                styles={{
                  container: (provided) => ({
                    ...provided,
                    width: '90%',
                    marginRight: '0',
                    textAlign: 'center',
                  }),
                }}
                isClearable={false}
              />
            </div>
          </div>
        )}

        {/* Display Error Message if Any */}
        {error && (
          <div className="row" style={{ marginBottom: '10px' }}>
            <div
              className="col-md-12"
              style={{ color: 'red', textAlign: 'center' }}
            >
              {error}
            </div>
          </div>
        )}

        {/* Chart Container */}
        <div className="chartContainer">
          {chartOptions &&
          chartOptions.series &&
          chartOptions.series.length > 0 ? (
            <ReactEcharts
              key={`${chartType.value}-${rollWindow}-${primaryFund}-${benchmarkFund}`} // Unique key to force re-render
              ref={(e) => (this.echartsRef = e)}
              option={chartOptions}
              notMerge={true}
              theme="default"
              style={{ height: 500, width: '100%' }} // Make the chart responsive
              onEvents={this.onChartEvents}
            />
          ) : (
            <div
              style={{ textAlign: 'center', color: '#8C959B', padding: '20px' }}
            >
              No data available for the selected chart type and roll window.
            </div>
          )}
        </div>
      </div>
    );
  }
}

// **Redux Mapping**
function mapStateToProps(state) {
  return {
    data: state.fundData.data, // Ensure that data is correctly populated in the Redux store
  };
}

export default connect(mapStateToProps, {})(RollingHorizons);
